import React, { useState } from 'react';
import { FormField } from './form/FormField';
import { TextAreaField } from './form/TextAreaField';
import { LocationDropdowns } from './LocationDropdowns';
import { PrivacyNotice } from './form/PrivacyNotice';
import { SubmitButton } from './form/SubmitButton';
import { SuccessMessage } from './form/SuccessMessage';
import { supabase, checkSupabaseConnection } from '../lib/supabase';
import { sendWaitlistConfirmation } from '../lib/email';
import { useGeolocation } from '../utils/useGeolocation';
import { useAnalytics } from '../hooks/useAnalytics';

export function WaitlistForm() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    country: '',
    state: '',
    featureSuggestions: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { country, state } = useGeolocation();
  const { trackWaitlistJoin } = useAnalytics();

  React.useEffect(() => {
    if (country && !formData.country) {
      setFormData(prev => ({
        ...prev,
        country,
        state: state || ''
      }));
    }
  }, [country, state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    if (!checkSupabaseConnection()) {
      setError('Database connection not configured');
      setIsSubmitting(false);
      return;
    }

    try {
      const { error: dbError } = await supabase
        .from('waitlist')
        .insert({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          country: formData.country,
          state: formData.state || null,
          feature_suggestions: formData.featureSuggestions || null
        });

      if (dbError) throw dbError;

      await sendWaitlistConfirmation({
        to: formData.email,
        firstName: formData.firstName
      });

      trackWaitlistJoin(formData.country);
      setIsSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join waitlist');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (isSuccess) {
    return <SuccessMessage />;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <FormField
          id="firstName"
          label="First Name"
          type="text"
          value={formData.firstName}
          onChange={e => handleChange('firstName', e.target.value)}
          required
        />
        <FormField
          id="lastName"
          label="Last Name"
          type="text"
          value={formData.lastName}
          onChange={e => handleChange('lastName', e.target.value)}
          required
        />
      </div>

      <FormField
        id="email"
        label="Email"
        type="email"
        value={formData.email}
        onChange={e => handleChange('email', e.target.value)}
        required
      />

      <LocationDropdowns
        country={formData.country}
        state={formData.state}
        onChange={handleChange}
      />

      <TextAreaField
        id="featureSuggestions"
        label="What features would you like to see?"
        value={formData.featureSuggestions}
        onChange={e => handleChange('featureSuggestions', e.target.value)}
        placeholder="Share your suggestions for features that would make CloseCircle more valuable for you..."
      />

      {error && (
        <p className="text-red-500 text-sm">{error}</p>
      )}

      <PrivacyNotice />
      <SubmitButton isSubmitting={isSubmitting} />
    </form>
  );
}